import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { normalizeReportUrl } from '../utils/report-access-url.util';

/**
 * Shared front half of every authenticated route guard.
 *
 * Repairs share links whose `?accessToken=` separator arrived percent-encoded
 * (see normalizeReportUrl) and, when the link carries a token, opens a report
 * session from it. Returns a UrlTree when the URL had to be rewritten, in which
 * case the caller must return it so the router re-runs the guard against the
 * clean URL and `:id` resolves to a real UUID instead of a token blob.
 */
function consumeReportToken(
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authService: AuthService,
  router: Router
): UrlTree | null {
  const normalized = normalizeReportUrl(state.url);

  if (normalized.wasMangled) {
    return router.parseUrl(normalized.url);
  }

  const accessToken = normalized.token ?? route.queryParams['accessToken'];
  if (accessToken) {
    authService.setReportSession(accessToken);
  }

  return null;
}

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Share links land on /documents/:id as often as on /reports/:id, so the
  // token has to be honoured here too - not only in reportAccessGuard.
  const redirect = consumeReportToken(route, state, authService, router);
  if (redirect) {
    return redirect;
  }

  if (authService.isAuthenticated()) {
    return true;
  } else {
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }
};

export const guestGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    return true;
  } else {
    router.navigate(['/manuscript-setup']);
    return false;
  }
};

export const reportAccessGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const redirect = consumeReportToken(route, state, authService, router);
  if (redirect) {
    return redirect;
  }

  if (authService.isAuthenticated()) {
    return true;
  } else {
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }
};
